import { SectionCaption } from '@app/core/components/SectionCaption';
import { UrlMediaEmbed } from '@app/core/media-player/UrlMediaEmbed';
import { mimeType } from '@app/helpers/utils';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { defineMessage, MessageDescriptor } from 'react-intl';
import {
  buildAuditMediaFileUrl,
  resolveAuditMediaIdByCustomId,
} from '../../helpers/build-audit-media-file-url';
import { AuditMatchAudio } from '../../types';
import { DownloadAudioBtn } from '../download-audio/DownloadAudioBtn';

const creativeTitleMessage = defineMessage({
  id: 'audit.verifyMatch.creativeTitle',
  defaultMessage: 'Media de referencia',
});

export interface VerifyMatchCreativePlayerProps {
  stationId: string;
  audio: AuditMatchAudio;
  /** Known media UUID; skips the customId lookup when provided. */
  mediaId?: string;
  /** Section caption; defaults to “Media de referencia”. */
  title?: MessageDescriptor;
}

/**
 * Embeds the uploaded audit creative (reference file) inside the verify dialog.
 * Resolves media UUID from customId via api-v2 when `mediaId` is not passed, then plays/downloads the file.
 */
export function VerifyMatchCreativePlayer({
  stationId,
  audio,
  mediaId: mediaIdProp,
  title = creativeTitleMessage,
}: VerifyMatchCreativePlayerProps) {
  const mediaQuery = useQuery({
    queryKey: ['audit-media-id', stationId, audio.customId],
    queryFn: () => resolveAuditMediaIdByCustomId(stationId, audio.customId),
    staleTime: 5 * 60 * 1000,
    enabled: !mediaIdProp,
  });

  const mediaId = mediaIdProp ?? mediaQuery.data;
  const url = mediaId ? buildAuditMediaFileUrl(mediaId) : undefined;
  const type = mimeType(audio.filename);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          '& .MuiTypography-root': { mb: 0 },
          '& .MuiIconButton-root': { p: 0.25 },
          '& .MuiSvgIcon-root': { fontSize: 20 },
        }}
      >
        <SectionCaption message={title} />
        {mediaId ? <DownloadAudioBtn mediaId={mediaId} size="small" /> : null}
      </Box>
      {url ? <UrlMediaEmbed url={url} type={type} /> : null}
    </Box>
  );
}
